import React, { useMemo, useState } from "react"
import { useQuery } from "@apollo/client"
import {
    Avatar,
    Box,
    CssBaseline,
    List,
    ListItem,
    Typography,
    useTheme,
    TextField,
    InputAdornment,
    IconButton,
} from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import { GET_NOTIFICATION_LIST_QUERY } from "../../entities/notification/graphql/queries"

// Define types for notification structure
interface Notification {
    _id: string
    message: string
    notificationTopic?: { name: string }
    _metadata?: { createdAt: string }
}

// GraphQL response type
interface NotificationsData {
    notificationsList: { records: Notification[] }
}

// Consolidated styles
const useStyles = (theme: any) => ({
    pageContainer: {
        height: { xs: "90vh", sm: "89vh", md: "88vh" },
        "@media (max-height: 600px)": { height: "87vh" },
        "@media (max-height: 500px)": { height: "85vh" },
    },
    searchInput: {
        width: "100%",
        bgcolor: "#103751",
        borderRadius: "25px",
        "& .MuiOutlinedInput-root": {
            height: "50px",
            paddingRight: "10px",
            "& fieldset": { border: "none" },
        },
        "& .MuiInputBase-input": { color: "white", padding: "14px 16px" },
    },
    searchIconButton: {
        bgcolor: "rgba(255, 255, 255, 0.1)",
        borderRadius: "50%",
        "&:hover": { bgcolor: "rgba(255, 255, 255, 0.2)" },
    },
    listContainer: { width: "100%", overflow: "auto", mt: 1.5 },
    noNotificationBox: {
        height: "70vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    avatar: { height: 60, width: 60, mr: 2, boxShadow: 2 },
    dateText: { width: "20%", textAlign: "end", pt: 1.5 },
})

// Notification Item Component
const NotificationItem: React.FC<{ notification: Notification }> = ({ notification }) => {
    const { notificationTopic, message, _metadata } = notification
    const createdAt = _metadata?.createdAt ? new Date(_metadata.createdAt).toLocaleDateString() : "N/A"

    return (
        <ListItem disableGutters sx={{ alignItems: "flex-start", mt: 1 }}>
            <Avatar sx={{ height: 60, width: 60, mr: 2, boxShadow: 2 }}>
                {notificationTopic?.name?.charAt(0) ?? "N"}
            </Avatar>
            <Box flex={1}>
                <Typography variant="subtitle1">{notificationTopic?.name || "Unknown"}</Typography>
                <Typography variant="body2">{message}</Typography>
            </Box>
            <Typography variant="body2" sx={{ textAlign: "end", pt: 1.5 }}>
                {createdAt}
            </Typography>
        </ListItem>
    )
}

const Notification: React.FC = () => {
    const theme = useTheme()
    const classes = useStyles(theme)
    const selectedSite = sessionStorage.getItem("selectedSite")

    // Query and state
    const {
        data: NotificationData,
        loading,
        error,
    } = useQuery<NotificationsData>(GET_NOTIFICATION_LIST_QUERY, { skip: !selectedSite })
    const [searchTerm, setSearchTerm] = useState("")

    // Filtered notifications
    const filteredNotifications = useMemo(() => {
        const notifications = NotificationData?.notificationsList.records ?? []
        if (!searchTerm.trim()) return notifications

        return notifications.filter(({ message, notificationTopic }) =>
            [message, notificationTopic?.name]
                .filter(Boolean)
                .some((field) => field!.toLowerCase().includes(searchTerm.toLowerCase()))
        )
    }, [NotificationData, searchTerm])

    // Render states
    if (loading) return <Typography>Loading...</Typography>
    if (error) return <Typography color="error">Error: {error.message}</Typography>

    return (
        <Box display="flex" justifyContent="center" sx={classes.pageContainer}>
            <CssBaseline />
            <Box
                width="100%"
                maxWidth={700}
                display="flex"
                flexDirection="column"
                paddingTop={{ xs: "10%", sm: "7%", md: "3%" }}
                paddingX={{ xs: "6%", sm: "4%", md: "2%" }}
            >
                {/* Search Bar */}
                <TextField
                    placeholder="Search Notifications"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton sx={classes.searchIconButton}>
                                    <SearchIcon sx={{ color: "white", fontSize: 22 }} />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                    sx={classes.searchInput}
                />

                {/* Notification List */}
                <Box sx={classes.listContainer}>
                    {filteredNotifications.length === 0 ? (
                        <Box sx={classes.noNotificationBox}>
                            <Typography variant="subtitle1" color="text.secondary">
                                No Notifications Available
                            </Typography>
                        </Box>
                    ) : (
                        <List>
                            {filteredNotifications.map((notification) => (
                                <NotificationItem key={notification._id} notification={notification} />
                            ))}
                        </List>
                    )}
                </Box>
            </Box>
        </Box>
    )
}

export default Notification
